import { IProjectRole } from "@kuika/kuika-cl-model";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { DeleteModal } from "../../../../common/delete-modal";
import { KModal } from "../../../../ui-kit/k-modal/k-modal";
import { RoleDragPreview } from "./role-drag-preview";
import { RoleFolderAddComponent } from "./role-folder-add";
import { RoleList } from "./role-list";
import { RoleManagementEmptyState } from "./role-management-empty-state";
import "./role-management-modal.scss";
import { RoleManagementTopBar } from "./role-management-top-bar";
import { RoleModal } from "./role-modal";
import { useDragAndDrop } from "./use-drag-and-drop";

export interface EditingState {
  type: "role" | "folder";
  id: string;
  name: string;
  startingScreenId?: string;
}

export interface DeleteState {
  isOpen: boolean;
  roleId: string;
  roleName: string;
}

export interface FolderEditState {
  isOpen: boolean;
  folderId?: string;
  folderName?: string;
  parentId?: string | null;
}

export interface DragPreview {
  role: IProjectRole;
  x: number;
  y: number;
}

export interface IRoleManagementModalProps {
  showModal: boolean;
  closeModal: () => void;
}

const RoleManagementModalCmp: React.FC<IRoleManagementModalProps & IWithSDProps> = ({ showModal, closeModal, sd }) => {
  const getAllFolderIds = useCallback((folders: any[]): string[] => {
    const ids: string[] = [];
    for (const folder of folders || []) {
      ids.push(folder.id);
      if (folder.children && folder.children.length > 0) {
        ids.push(...getAllFolderIds(folder.children));
      }
    }
    return ids;
  }, []);

  const roles: IProjectRole[] = sd.project.roles || [];
  const screens = sd.screenList || [];
  const emptyRoleFolders: any[] = useMemo(() => [], []);
  const roleFolders: any[] = useMemo(() => {
    const rf = (sd.project as any)?.roleFolders;
    return rf ?? emptyRoleFolders;
  }, [sd.project?.roleFolders, emptyRoleFolders]);

  const [search, setSearch] = useState("");
  const [expandedIds, setExpandedIds] = useState<string[]>(() => getAllFolderIds(roleFolders));

  useEffect(() => {
    const nextExpanded = getAllFolderIds(roleFolders);
    setExpandedIds((prev) => {
      if (prev === nextExpanded) return prev;
      if (prev.length === nextExpanded.length && prev.every((id, i) => id === nextExpanded[i])) return prev;
      return nextExpanded;
    });
  }, [roleFolders, getAllFolderIds]);

  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [activeFolderId, setActiveFolderId] = useState<string | undefined>(undefined);
  const activeFolderIdRef = useRef<string | undefined>(undefined);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [deleteState, setDeleteState] = useState<DeleteState>({
    isOpen: false,
    roleId: "",
    roleName: ""
  });
  const [folderEditState, setFolderEditState] = useState<FolderEditState>({
    isOpen: false
  });

  const { dragPreview, onDragStart, onDragEnd } = useDragAndDrop(sd, roles, roleFolders);

  const isRoleInAnyFolder = useCallback((roleId: string, folders: any[]): boolean => {
    for (const f of folders || []) {
      if ((f.roleIds || []).includes(roleId)) return true;
      if (f.children && f.children.length && isRoleInAnyFolder(roleId, f.children)) return true;
    }
    return false;
  }, []);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const findFolderById = useCallback((folders: any[], id?: string): any | undefined => {
    if (!id) return undefined;
    for (const folder of folders || []) {
      if (folder.id === id) return folder;
      if (folder.children && folder.children.length > 0) {
        const found = findFolderById(folder.children, id);
        if (found) return found;
      }
    }
    return undefined;
  }, []);

  const startInlineEditRole = useCallback((role: IProjectRole) => {
    setEditing({
      type: "role",
      id: role.id,
      name: role.name,
      startingScreenId: role.startingScreenId
    });
  }, []);

  const confirmInlineEditRole = useCallback(
    (role: IProjectRole) => {
      if (!editing) return;
      // The name is read-only here; keep it verbatim so a role never gets renamed as a side effect
      // of picking a starting screen.
      sd.updateRole({
        id: role.id,
        name: role.name,
        startingScreenId: editing.startingScreenId
      });
      setEditing(null);
    },
    [editing, sd]
  );

  const cancelInlineEdit = useCallback(() => setEditing(null), []);

  const updateEditingField = useCallback((field: keyof EditingState, value: string) => {
    setEditing((prev) => (prev ? { ...prev, [field]: value } : null));
  }, []);

  const openAddRole = useCallback((folderId?: string) => {
    setActiveFolderId(folderId);
    activeFolderIdRef.current = folderId;
    setRoleModalVisible(true);
  }, []);

  const closeAddRole = useCallback(() => {
    setRoleModalVisible(false);
  }, []);

  // Awaited, so the New Role dialog keeps its spinner until the save is back.
  const handleRoleAdded = useCallback(
    async (data: IProjectRole): Promise<void> => {
      await sd.updateRole(data);
      const folderId = activeFolderIdRef.current ?? activeFolderId;
      if (data?.id && folderId) {
        const currentRoleFolders = ((sd.project as any)?.roleFolders || []) as any[];
        const targetFolder = findFolderById(currentRoleFolders, folderId);
        if (targetFolder?.parentFolderId) {
          sd.addRoleToFolderInFolder?.(targetFolder.parentFolderId, targetFolder.id, data.id);
        } else {
          sd.addRoleToFolder?.(folderId, data.id);
        }
      }
      // The dialog closes itself through `closeAddRole` once CREATE's ✓ has been read; closing it
      // here would take the ✓ with it.
      setActiveFolderId(undefined);
      activeFolderIdRef.current = undefined;
    },
    [sd, activeFolderId, findFolderById]
  );

  const handleDeleteRole = useCallback((role: IProjectRole) => {
    setDeleteState({
      isOpen: true,
      roleId: role.id,
      roleName: role.name
    });
  }, []);

  const confirmDeleteRole = useCallback(() => {
    sd.deleteRole?.(deleteState.roleId);
    setDeleteState({ isOpen: false, roleId: "", roleName: "" });
  }, [sd, deleteState.roleId]);

  const cancelDeleteRole = useCallback(() => {
    setDeleteState({ isOpen: false, roleId: "", roleName: "" });
  }, []);

  const handleDeleteFolder = useCallback(
    (folder: any) => {
      sd.deleteRoleFolder?.(folder.id);
    },
    [sd]
  );

  const handleEditFolder = useCallback((folder: any) => {
    setFolderEditState({
      isOpen: true,
      folderId: folder.id,
      folderName: folder.name,
      parentId: folder.parentFolderId
    });
  }, []);

  const openAddFolderModal = useCallback((parentId?: string | null) => {
    setFolderEditState({
      isOpen: true,
      parentId: parentId ?? null
    });
  }, []);

  const handleAddSubFolder = useCallback(
    (parentFolderId: string) => {
      openAddFolderModal(parentFolderId);
    },
    [openAddFolderModal]
  );

  const closeFolderModal = useCallback(() => {
    setFolderEditState({ isOpen: false });
  }, []);

  const hasContent = roleFolders.length > 0 || roles.length > 0;

  return (
    <>
      <KModal showModal={showModal} closeModal={closeModal} header={"ROLE MANAGEMENT"} width={600}>
        <div className="role-management-modal-content">
          <RoleManagementTopBar
            search={search}
            onSearchChange={setSearch}
            onAddFolder={() => openAddFolderModal(null)}
            onAddRole={() => openAddRole()}
          />
          {hasContent ? (
            <RoleList
              roles={roles}
              roleFolders={roleFolders}
              search={search}
              expandedIds={expandedIds}
              editing={editing}
              screens={screens}
              isRoleInAnyFolder={isRoleInAnyFolder}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onToggleExpand={toggleExpand}
              onAddRole={openAddRole}
              onAddSubFolder={handleAddSubFolder}
              onEditFolder={handleEditFolder}
              onDeleteFolder={handleDeleteFolder}
              onStartEditRole={startInlineEditRole}
              onConfirmEditRole={confirmInlineEditRole}
              onCancelEdit={cancelInlineEdit}
              onUpdateField={updateEditingField}
              onDeleteRole={handleDeleteRole}
            />
          ) : (
            <RoleManagementEmptyState />
          )}
        </div>
      </KModal>

      <RoleDragPreview dragPreview={dragPreview} />

      <RoleFolderAddComponent
        showModal={folderEditState.isOpen && !folderEditState.folderId}
        closeModal={closeFolderModal}
        showEditModal={folderEditState.isOpen && !!folderEditState.folderId}
        closeEditModal={closeFolderModal}
        editingFolderId={folderEditState.folderId}
        editingFolderName={folderEditState.folderName}
        projectId={sd?.project?.id}
        componentSetId={sd?.project?.componentSetObj?.id}
        folderParentFolderId={folderEditState.parentId}
      />

      <RoleModal
        showModal={roleModalVisible}
        closeModal={closeAddRole}
        onChange={handleRoleAdded}
        name=""
        id=""
        startingScreenId=""
      />

      <DeleteModal
        itemName={deleteState.roleName}
        triggerModal={deleteState.isOpen}
        deleteItem={confirmDeleteRole}
        deleteType="Role"
        closeModal={cancelDeleteRole}
      />
    </>
  );
};

export const RoleManagementModal = withSD<IRoleManagementModalProps>(RoleManagementModalCmp);
